import { useMemo, useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Checkbox, Popover } from '@mantine/core';
import { IconFileText, IconGift, IconPackages } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { Reservation, ServiceBooking } from '@turizi/shared';
import { RESERVATIONS_PATH, SERVICE_ORDERS_PATH, SERVICE_PATH, SERVICES_PATH } from '@/lib/navigation';
import { RESERVATION_STATUS } from '@turizi/shared';
import { formatDayShort, formatPrice } from '@/lib/format-utils';
import { TagIcon } from '@/lib/tag-utils';
import { ReservationClient, ReservationPaymentStatus } from './reservation-ui';
import BookingPaxsBookings from './service-booking/booking-paxs-bookings';
import BookingServiceOrders from './service-booking/booking-service-orders';
import { BookingGroupSize, BookingGroupSizeSlim, ExternalCreatedByIndicator } from './snippets';
import ProviderSelector from './service-booking/provider-selector';
import CoordinatorSelector from './service-booking/coordinator-selector';
import { ServiceTitleStatic } from './services/service-title';
import ShowSelfAttrs from './show-self-attrs';
import ServiceDatePopover from './service-date-popover';
import SelectTimePopover from './select-time-popover';
import { useUpdateServiceBooking, useProducts } from '@/hooks/api-hooks';
import { sortServiceBookings } from '@/lib/bookings-helper';
import { localTenant } from '@/services/session-service';

const getColClassName = (colId: string, slim?: boolean) => {
    switch (colId) {
        case 'toggle': return 'w-[45px] shrink-0 text-center';
        case 'bookingId': return 'w-[45px] shrink-0 text-right';
        case 'reservation': return 'w-[140px] shrink-0';
        case 'paxs': return slim ? 'flex-1 min-w-[320px]' : 'flex-1 min-w-[420px]';
        case 'order': return 'min-w-[60px] shrink-0';
        case 'date': return 'w-[60px] shrink-0';
        case 'hour': return 'w-[55px] shrink-0';
        case 'provider': return 'w-[130px] shrink-0';
        case 'coordinator': return 'w-[130px] shrink-0';
        case 'service': return 'flex-2 min-w-[220px]';
        case 'attributes': return 'flex-1 min-w-[180px]';
        case 'cost': return 'min-w-[130px] shrink-0 text-right';
        case 'price': return 'min-w-[130px] shrink-0 text-right';
        case 'payment_status': return 'min-w-[180px] shrink-0 flex justify-end';
        default: return 'flex-1';
    }
};

interface Props {
    bookings: ServiceBooking[];
    reservation?: Reservation;
    showByCategory?: boolean;
    showCategory?: boolean; // Keep for backwards compatibility
    showByService?: boolean;
    showByProduct?: boolean;
    showServiceOrder?: boolean;
    showReservation?: boolean;
    showClientName?: boolean;
    showPrice?: boolean;
    showCost?: boolean;
    showPaymentStatus?: boolean;
    showPaxs?: boolean;
    showPhone?: boolean;
    showPaxName?: boolean;
    showHour?: boolean;
    showProvider?: boolean;
    showCoordinator?: boolean;
    showBookingId?: boolean;
    editable?: boolean;
    hideServicePackage?: boolean;
    slim?: boolean;
    selectedAttrKeys?: string[];
    selectedIds?: number[];
    showHeader?: boolean;
    showDiscrepancy?: boolean;
    onPaxsClick?: (booking: ServiceBooking) => void;
    onSelectRow?: (id: number) => void;
}

interface ByCategoryHeaderProps {
    groupKey: string;
    groupBookings: ServiceBooking[];
    t: (key: string) => string;
}

function ByCategoryHeader({ groupKey, groupBookings, t }: ByCategoryHeaderProps) {
    return (
        <div className="ml-4 flex items-center text-sm gap-2 mb-2">
            <TagIcon size="md" name={groupKey} hideText />
            <span className="text-blue-900 font-extrabold">{t(`${groupKey}_plural`)}</span>
        </div>
    );
}

interface ByServiceHeaderProps {
    groupBookings: ServiceBooking[];
    t: (key: string) => string;
}

function ByServiceHeader({ groupBookings, t }: ByServiceHeaderProps) {
    const firstBooking = groupBookings[0];
    const serviceData = firstBooking?.service_data;
    const serviceName = ServiceTitleStatic(serviceData);
    const serviceCategory = serviceData?.category || 'other';

    return (
        <div className="flex flex-wrap gap-6 items-center justify-between w-full px-4 py-2.5 bg-slate-800 text-white rounded-t-md">
            <div className="font-bold flex items-center gap-2">
                <TagIcon size="xs" name={serviceCategory} hideText />
                <span className='text-sm'>{serviceName}</span>
                <span className="font-normal text-xs">#{serviceData.id}</span>
            </div>

            <div className="bg-white px-2.5 py-1 rounded-md text-xs shadow-xs">
                <BookingGroupSizeSlim
                    booking={groupBookings.reduce(
                        (acc, b) => {
                            acc.adults += b.adults || 0;
                            acc.children += b.children || 0;
                            acc.seniors += b.seniors || 0;
                            acc.infants += b.infants || 0;
                            acc.guests += b.guests || 0;
                            return acc;
                        },
                        { adults: 0, children: 0, seniors: 0, infants: 0, guests: 0 }
                    )}
                />
            </div>
        </div>
    );
}

interface ByProductHeaderProps {
    groupKey: string;
    groupBookings: ServiceBooking[];
    t: (key: string) => string;
}

function ByProductHeader({ groupKey, groupBookings, t }: ByProductHeaderProps) {
    const isNoProduct = groupKey === 'no_product';
    const productName = isNoProduct ? t('no_product') : groupKey;

    return (
        <div className="flex flex-wrap gap-6 items-center w-full px-4 py-2.5 bg-slate-900 text-white rounded-t-md">
            <div className="font-bold flex items-center gap-2">
                <IconPackages size={18} className='text-gray-500' />
                <span className="text-sm">{productName}</span>
            </div>
            <BookingGroupSizeSlim
                booking={groupBookings.reduce(
                    (acc, b) => {
                        acc.adults += b.adults || 0;
                        acc.children += b.children || 0;
                        acc.seniors += b.seniors || 0;
                        acc.infants += b.infants || 0;
                        acc.guests += b.guests || 0;
                        return acc;
                    },
                    { adults: 0, children: 0, seniors: 0, infants: 0, guests: 0 }
                )}
            />
        </div>
    );
}

export default function ServiceBookingsList({
    bookings,
    reservation,
    showByCategory,
    showCategory = true,
    showByService = false,
    showByProduct = false,
    showServiceOrder = true,
    showReservation = true,
    showClientName = true,
    showPrice = false,
    showCost = false,
    showPaymentStatus = true,
    showPaxs = true,
    showPhone = true,
    showPaxName = true,
    showHour = true,
    showProvider = true,
    showCoordinator = true,
    showBookingId = true,
    editable = false,
    hideServicePackage = true,
    slim = false,
    selectedAttrKeys,
    selectedIds,
    // showHeader = true,
    showDiscrepancy = true,
    onSelectRow,
    onPaxsClick,
}: Props): JSX.Element {
    const { t } = useTranslation();
    const shouldGroupByCategory = showByCategory !== undefined ? showByCategory : showCategory;

    const { data: productsData } = useProducts(undefined, { enabled: !!showByProduct });
    const products = productsData?.data || [];

    const serviceIdToProductMap = useMemo(() => {
        const map = new Map<number, string>();
        if (!showByProduct || !products.length) return map;

        products.forEach((prod) => {
            if (prod.name && Array.isArray(prod.services)) {
                prod.services.forEach((svc) => {
                    if (svc.id) {
                        map.set(svc.id, prod.name);
                    }
                });
            }
        });

        return map;
    }, [products, showByProduct]);

    const groupedbookings = useMemo(() => {
        let filtered = bookings || [];

        if (hideServicePackage) {
            filtered = filtered.filter(booking => !(booking.service_data?.child_services?.length > 0));
        }

        if (!shouldGroupByCategory && !showByService && !showByProduct) {
            const sorted = sortServiceBookings(filtered);
            return new Map<string, ServiceBooking[]>([['all', sorted]]);
        }

        const grouped = filtered.reduce((acc: Map<string, ServiceBooking[]>, booking: ServiceBooking) => {
            let groupKey: string;

            if (showByProduct) {
                const serviceId = booking.service_id || booking.service_data?.id || booking.service?.id;
                const mappedProductName = serviceId ? serviceIdToProductMap.get(serviceId) : undefined;
                const serviceTitle = ServiceTitleStatic(booking.service_data || booking.service);
                groupKey = mappedProductName || booking.service_data?.product?.name || booking.service?.product?.name || serviceTitle || t('no_product');
            } else if (showByService) {
                groupKey = booking.service_id?.toString() || 'unknown';
            } else {
                groupKey = booking.service_data?.category || 'other';
            }

            if (!acc.has(groupKey)) {
                acc.set(groupKey, []);
            }
            acc.get(groupKey)!.push(booking);
            return acc;
        }, new Map<string, ServiceBooking[]>());

        grouped.forEach((categoryBookings, category) => {
            grouped.set(category, sortServiceBookings(categoryBookings));
        });

        const getGroupPaxCount = (bookingsList: ServiceBooking[]) => {
            return bookingsList.reduce((sum, b) => {
                const groupPaxCount = (b.pax_groups || []).reduce((acc, bg) => {
                    return acc + ((bg.adults || 0) + (bg.children || 0) + (bg.seniors || 0) + (bg.infants || 0) + (bg.guests || 0));
                }, 0);
                const count = Math.max(
                    (b.adults || 0) + (b.children || 0) + (b.seniors || 0) + (b.guests || 0) + (b.infants || 0),
                    groupPaxCount
                );
                return sum + count;
            }, 0);
        };

        // Sort categories, services or products by total pax count (highest first)
        const sortedEntries = Array.from(grouped.entries()).sort(([keyA, bookingsA], [keyB, bookingsB]) => {
            const paxA = getGroupPaxCount(bookingsA);
            const paxB = getGroupPaxCount(bookingsB);

            if (paxA !== paxB) {
                return paxB - paxA; // Highest total pax count first
            }

            if (showByProduct) {
                if (keyA === 'no_product') return 1;
                if (keyB === 'no_product') return -1;
                return keyA.localeCompare(keyB);
            } else if (showByService) {
                const nameA = bookingsA[0]?.service_data?.name || '';
                const nameB = bookingsB[0]?.service_data?.name || '';
                return nameA.localeCompare(nameB);
            } else {
                if (keyA === 'service_group') return -1;
                if (keyB === 'service_group') return 1;
                if (keyA === 'stay') return -1;
                if (keyB === 'stay') return 1;
                return keyA.localeCompare(keyB);
            }
        });

        return new Map<string, ServiceBooking[]>(sortedEntries);
    }, [bookings, hideServicePackage, shouldGroupByCategory, showByService, showByProduct, serviceIdToProductMap]);


    return (
        <div className="space-y-4">
            {Array.from(groupedbookings.entries()).map(([groupKey, groupBookings]) => (
                <div key={groupKey} className="border border-gray-300 rounded-md hover:shadow-xl hover:border-gray-400">
                    {/* Category, Service or Product Header */}
                    {showByProduct ? (
                        <ByProductHeader groupKey={groupKey} groupBookings={groupBookings} t={t} />
                    ) : showByService ? (
                        <ByServiceHeader groupBookings={groupBookings} t={t} />
                    ) : shouldGroupByCategory ? (
                        <ByCategoryHeader groupKey={groupKey} groupBookings={groupBookings} t={t} />
                    ) : null}
                    <div className="overflow-scroll w-full">
                        {/* Data Rows */}
                        <div className="divide-y divide-slate-100">
                            {groupBookings.map((booking) => (
                                <ServiceBookingRowGrid
                                    key={booking.id}
                                    booking={booking}
                                    reservation={reservation}
                                    isSelected={selectedIds?.includes(booking.id)}
                                    onSelectRow={onSelectRow}
                                    showServiceOrder={showServiceOrder}
                                    showReservation={showReservation}
                                    showProvider={showProvider}
                                    showCoordinator={showCoordinator}
                                    showByService={showByService}
                                    showClientName={showClientName}
                                    showPrice={showPrice}
                                    showCost={showCost}
                                    showPaymentStatus={showPaymentStatus}
                                    showPaxs={showPaxs}
                                    showPaxName={showPaxName}
                                    showBookingId={showBookingId}
                                    showPhone={showPhone}
                                    showHour={showHour}
                                    editable={editable}
                                    onPaxsClick={onPaxsClick}
                                    selectedAttrKeys={selectedAttrKeys}
                                    showDiscrepancy={showDiscrepancy}
                                    slim={slim}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            ))
            }
        </div >
    );
}

interface RowProps {
    booking: ServiceBooking;
    reservation?: Reservation;
    showServiceOrder?: boolean;
    showReservation?: boolean;
    showProvider?: boolean;
    showCoordinator?: boolean;
    showClientName?: boolean;
    showByService?: boolean;
    showPrice?: boolean;
    showCost?: boolean;
    showPaymentStatus?: boolean;
    showPaxs?: boolean;
    showPaxName?: boolean;
    showPhone?: boolean;
    showHour?: boolean;
    showBookingId?: boolean;
    editable?: boolean;
    selectedAttrKeys?: string[];
    isSelected?: boolean;
    showDiscrepancy?: boolean;
    onPaxsClick?: (booking: ServiceBooking) => void;
    onSelectRow?: (id: number) => void;
    slim?: boolean;
}

function ServiceBookingRowGrid({
    booking,
    reservation,
    showServiceOrder,
    showReservation,
    showProvider,
    showCoordinator,
    showClientName,
    showByService = true,
    showPrice,
    showCost,
    showPaymentStatus,
    showPaxs,
    showPaxName,
    showPhone,
    showHour,
    showBookingId,
    editable,
    selectedAttrKeys,
    isSelected,
    showDiscrepancy = true,
    onPaxsClick,
    onSelectRow,
    slim,
}: RowProps) {
    const [showBookings, setShowBookings] = useState(false);
    const { mutate: updateMutation, isPending: saving } = useUpdateServiceBooking();

    const service = booking.service_data;
    const serviceOrder = booking.service_order_id;
    const reservationStatus = booking?.reservation?.status || reservation?.status;
    const reservationStartDate = booking?.reservation?.start_day || reservation?.start_day;
    const reservationEndDate = booking?.reservation?.end_day || reservation?.end_day;

    const handleBookingDateChange = (value: [string | undefined, string | undefined]) => {
        updateMutation({
            id: booking.id,
            start_day: value[0],
            end_day: value[1],
        });
    };

    const handleStartHourChange = (value: string) => {
        updateMutation({
            id: booking.id,
            start_hour: value,
        });
    };

    return (
        <div className={`
                flex gap-3 items-start px-4 py-3 hover:bg-indigo-50/50 transition-colors duration-150 
                rounded-md
                ${isSelected ? 'bg-orange-50/70' : 'bg-white'
            }`}
        >
            {/* 1. Toggle/Action */}
            {(onSelectRow) && (
                <div className={getColClassName('toggle', slim)}>
                    <Checkbox
                        checked={!!isSelected}
                        onChange={() => onSelectRow(booking.id)}
                    />
                </div>
            )}

            {/* Booking ID */}
            {showBookingId && (
                <div className={`${getColClassName('bookingId', slim)} flex flex-col items-center gap-1 font-medium text-xxs text-gray-400`}>
                    #{booking.id}
                    <ExternalCreatedByIndicator createdBy={booking?.created_by} />
                </div>
            )}

            {/* 2. Reservation */}
            {showReservation && !serviceOrder && (
                <div className={`${getColClassName('reservation', slim)} flex flex-col gap-0.5 text-gray-500 hover:text-blue-600 transition-colors group/res cursor-pointer min-w-0`}>
                    <Link
                        to={`${RESERVATIONS_PATH}/${booking?.reservation?.id || reservation?.id}`}
                        className="block flex gap-1 items-center font-medium text-xs"
                        target="blank"
                    >
                        <Badge
                            circle
                            color={RESERVATION_STATUS[reservationStatus]?.color || 'gray'}
                            className="!w-2 !h-2 !p-0 shrink-0"
                        />
                        <span>#{booking?.reservation_id}</span>
                    </Link>
                    <span className="text-[10px] text-gray-400 font-semibold whitespace-nowrap">
                        {formatDayShort(reservationStartDate)} - {formatDayShort(reservationEndDate)}
                    </span>
                    {showClientName && (
                        <div className='font-bold text-xs'>
                            <ReservationClient reservation={reservation || booking.reservation} />
                        </div>
                    )}
                </div>
            )}

            {/* 5. Date */}
            <div className={getColClassName('date', slim)}>
                <ServiceDatePopover
                    size="xs"
                    limits={[
                        reservation?.start_day || booking?.reservation?.start_day,
                        reservation?.end_day || booking?.reservation?.end_day
                    ]}
                    serviceCategory={service.category}
                    value={[booking.start_day, booking.end_day]}
                    onChange={handleBookingDateChange}
                    loading={saving}
                    disabled={!editable}
                />
            </div>

            {/* 6. Hour */}
            {showHour && (
                <div className={getColClassName('hour', slim)}>
                    <SelectTimePopover
                        size="xs"
                        value={booking.start_hour}
                        onChange={handleStartHourChange}
                        loading={saving}
                        disabled={!editable}
                    />
                </div>
            )}

            {/* 3. Paxs */}
            {showPaxs && (
                // this is used in the Manage Service Order, to open the allocation Modal
                <div className={getColClassName('paxs', slim)}>
                    {onPaxsClick ? (
                        <div
                            className="cursor-pointer"
                            onClick={(e) => {
                                e.stopPropagation();
                                onPaxsClick(booking);
                            }}
                        >
                            <BookingGroupSize
                                booking={booking}
                                reservation={reservation || booking.reservation}
                            />
                        </div>
                    ) : (
                        <Popover
                            opened={showBookings}
                            onChange={setShowBookings}
                            keepMounted={false}
                            shadow="sm"
                            radius="lg"
                            withArrow={true}
                            closeOnClickOutside={true}
                            closeOnEscape={true}
                            clickOutsideEvents={['mousedown', 'touchstart']}
                        >
                            <Popover.Target>
                                <div
                                    className="cursor-pointer"
                                    onClick={() => setShowBookings(o => !o)}
                                >
                                    <BookingGroupSize
                                        showPaxFirst={false}
                                        showPaxName={showPaxName}
                                        booking={booking}
                                        showPhone={showPhone}
                                        showDiscrepancy={showDiscrepancy}
                                        reservation={reservation || booking.reservation}
                                    />
                                </div>
                            </Popover.Target>
                            <Popover.Dropdown>
                                <BookingPaxsBookings booking={booking} />
                            </Popover.Dropdown>
                        </Popover>
                    )}
                </div>
            )}

            {/* 8. Attributes */}
            <div className={getColClassName('attributes', slim)}>
                <ShowSelfAttrs
                    attrs={booking.self_attrs}
                    context={booking}
                    size="xs"
                    include={selectedAttrKeys}
                    showkey={false}
                />
                {booking.observations && (
                    <div className="text-xs solid-box-border-negative mt-1 w-fit">
                        {booking.observations}
                    </div>
                )}
            </div>

            {showByService === false && (
                <div className={`${getColClassName('service', slim)} text-xs whitespace-normal break-words`}>
                    <div className="flex items-start gap-1 font-medium text-slate-800">
                        <TagIcon name={booking.service_data?.category} size="xxs" hideText />
                        <div>
                            {ServiceTitleStatic(service)}
                            <span className="text-gray-600 bg-gray-200 rounded-sm px-0.5 py-0.5 font-semibold text-[10px] ml-1">
                                #{service.id}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* 4. Service Order */}
            {showServiceOrder && (
                <div className={`${getColClassName('order', slim)} flex items-center`}>
                    <BookingServiceOrders
                        booking={booking}
                        editable={editable}
                    />
                </div>
            )}

            {/* 6.5. Provider */}
            {showProvider && (
                <div className={getColClassName('provider', slim)}>
                    <ProviderSelector
                        booking={booking}
                        size="xs"
                        editable={editable}
                    />
                </div>
            )}

            {/* 6.6. Coordinator */}
            {showCoordinator && (
                <div className={getColClassName('coordinator', slim)}>
                    <CoordinatorSelector
                        booking={booking}
                        size="xs"
                        editable={editable}
                    />
                </div>
            )}

            {/* 11. Payment Status */}
            {showPaymentStatus && (
                <div className={`
                    ${getColClassName('payment_status', slim)}
                    solid-box-border-tinted
                `}>
                    <ReservationPaymentStatus reservation={reservation || booking.reservation} />
                </div>
            )}

            {/* 9. Cost */}
            {showCost && (
                <div className={`${getColClassName('cost', slim)} text-xs font-medium text-gray-800 flex items-baseline justify-end`}>
                    <span className="font-light text-red-400 ">$</span>{formatPrice(booking.money_out_amount)}<span className=" text-gray-400 uppercase">{booking.money_out_currency}</span>
                </div>
            )}

            {/* 10. Price */}
            {showPrice && (
                <div className={getColClassName('price', slim)}>
                    {booking.parent_booking_id ? (
                        <div className="flex justify-end p-2">
                            <IconGift size={16} stroke={1} color="gray" />
                        </div>
                    ) : (
                        <div className="font-medium text-gray-800 flex items-baseline justify-end">
                            <span className="font-light text-gray-400 ">$</span>{formatPrice(booking.money_in_amount)}<span className=" text-gray-400 uppercase">{booking.money_in_currency}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
